/**
 * Wall-clock dates and times of a time zone (the organization's), independent of the
 * browser's zone. Dates are `YYYY-MM-DD` and times `HH:MM`; instants are `Date`/ISO strings.
 * The API keeps the same rules: "today" and "overdue" are read in the organization's zone.
 */

export interface ZonedParts {
  year: number;
  /** 1–12. */
  month: number;
  day: number;
  hour: number;
  minute: number;
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad = (value: number) => String(value).padStart(2, '0');

/** The wall-clock parts of an instant in `timeZone`. */
export function zonedParts(instant: Date, timeZone: string): ZonedParts & { second: number } {
  const parts = Object.fromEntries(
    formatterFor(timeZone)
      .formatToParts(instant)
      .map((part) => [part.type, part.value]),
  );
  return {
    year: Number(parts['year']),
    month: Number(parts['month']),
    day: Number(parts['day']),
    hour: Number(parts['hour']) % 24,
    minute: Number(parts['minute']),
    second: Number(parts['second']),
    weekday: WEEKDAYS.indexOf(parts['weekday'] ?? 'Sun'),
  };
}

/** The date of an instant in `timeZone`, as `YYYY-MM-DD`. */
export function zonedDate(instant: Date, timeZone: string): string {
  const { year, month, day } = zonedParts(instant, timeZone);
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** The time of an instant in `timeZone`, as `HH:MM`. */
export function zonedTime(instant: Date, timeZone: string): string {
  const { hour, minute } = zonedParts(instant, timeZone);
  return `${pad(hour)}:${pad(minute)}`;
}

/** How far `timeZone` is ahead of UTC at an instant, in milliseconds. */
function offsetAt(instant: Date, timeZone: string): number {
  const parts = zonedParts(instant, timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** The instant of a wall-clock date and time (`00:00` by default) in `timeZone`. */
export function zonedInstant(date: string, time: string | null, timeZone: string): Date {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  const [hour = 0, minute = 0] = (time ?? '00:00').split(':').map(Number);
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  const first = guess - offsetAt(new Date(guess), timeZone);
  // A second pass settles the offset across a daylight-saving change.
  return new Date(guess - offsetAt(new Date(first), timeZone));
}

/** `date` plus `days` days (calendar arithmetic, no time zone involved). */
export function addDays(date: string, days: number): string {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return next.toISOString().slice(0, 10);
}

/** The weekday of a date: 0 = Sunday … 6 = Saturday. */
export function weekdayOf(date: string): number {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** The Monday on or before `date` (weeks start on Monday). */
export function startOfWeek(date: string): string {
  return addDays(date, -((weekdayOf(date) + 6) % 7));
}

/** The first day of the month of `date`. */
export function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

/** The same day `months` months later, clamped to the end of the month. */
export function addMonths(date: string, months: number): string {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  const first = new Date(Date.UTC(year, month - 1 + months, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  first.setUTCDate(Math.min(day, last));
  return first.toISOString().slice(0, 10);
}

/** Minutes since midnight of `HH:MM`. */
export function minutesOf(time: string): number {
  const [hour = 0, minute = 0] = time.split(':').map(Number);
  return hour * 60 + minute;
}

/** `HH:MM` of minutes since midnight. */
export function timeOf(minutes: number): string {
  const clamped = Math.max(0, Math.min(minutes, 24 * 60 - 1));
  return `${pad(Math.floor(clamped / 60))}:${pad(clamped % 60)}`;
}

/** Whether `value` is a real calendar date `YYYY-MM-DD`. */
export function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return addDays(value, 0) === value;
}

/** Minutes since midnight now, on the wall clock of `timeZone`. */
export function zonedMinutesNow(timeZone: string, now: Date = new Date()): number {
  const { hour, minute } = zonedParts(now, timeZone);
  return hour * 60 + minute;
}
